import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ISimpleNamedEntitiesRepo } from '../interfaces/isimpleNamedEntities.repo';
import { CachedRecord } from '../CachedRecord';
import { MarshalModelFactory } from '@socrates/common';
import { Model } from '@socrates/common';

@Injectable()
export class SimpleNamedEntitiesRepo<T extends Model> extends ISimpleNamedEntitiesRepo<T> {
	private myType: any;
	private http: HttpClient;
	private modelFactory: MarshalModelFactory<T>;
	private restPath: string;
	private cache: CachedRecord<T>;
	private _loadPromise: Promise<T[]> | null = null;
	private _initialized = false;

	constructor() {
		super();
	}

	initialize(httpClient: HttpClient, restPath: string, inputType: any, modelFactory: MarshalModelFactory<T>): void {
		if (!this._initialized) {
			this._initialized = true;
			this.restPath = restPath;
			this.http = httpClient;
			this.myType = inputType;
			this.modelFactory = modelFactory;
		}
	}

	async loadEntities(fullObject = false): Promise<T[]> {
		if (!this._initialized) throw new Error(`${this.myType?.name ?? 'Unknown'}Repo not initialized`);
		if (this.cache && !this.cache.expired) return this.cache.records;
		if (this._loadPromise) return this._loadPromise;

		const path = fullObject ? `${this.restPath}?full=true` : this.restPath;
		this._loadPromise = firstValueFrom(this.http.get(path))
			.then((response: any) => {
				if (!response || response.reason) throw response;
				const values: T[] = (response as any[]).map((v) => this.modelFactory.createNew(v));
				this.cache = new CachedRecord(this.modelFactory, 60 * 480 * 1000, values); // 8 hrs
				return values;
			})
			.finally(() => { this._loadPromise = null; });

		return this._loadPromise;
	}

	async getEntity(entityId: string): Promise<T> {
		if (!this._initialized) throw new Error(`${this.myType?.name ?? 'Unknown'}Repo not initialized`);
		if (this.cache && !this.cache.expired) {
			const cached = this.cache.getRecord(entityId);
			if (cached) return cached;
		}
		await this.loadEntities(true);
		return this.cache.getRecord(entityId);
	}

	get initialized(): boolean {
		return this._initialized;
	}
}
